import React from "react";
import { Image } from "@wss/image";
import { cls } from "@wss/cls";
import { VariationGroup, VariationGroupItem } from "./ProductDetailPageResult";
import variationAnalyticsClickTracking from "../analytics/PdpPageTracking/variationAnalyticsClickTracking";
import DisplayTypes from "./DisplayTypes";
import {
  baseTileStyling,
  baseTileBorderStyling,
  baseTileHoverStyling,
  selectedTileBorderStyling,
} from "~/ProductDetails/components/VariationTile/shared/styles/Tiles.styles";

interface ThumbnailVariationItemProps {
  groupItem: VariationGroupItem;
  isSelected: boolean;
  variationGroup: VariationGroup;
  getHrefTarget: (groupItem: VariationGroupItem, isSelected: boolean) => string;
  updateActiveItemNumber: (itemNumber: string | undefined) => void;
  onFocus: (itemNumber: string) => void;
  onBlur: (itemNumber: string) => void;
  displayTypeId?: number;
}

const ThumbnailVariationItem = ({
  groupItem,
  isSelected,
  variationGroup,
  getHrefTarget,
  updateActiveItemNumber,
  onFocus,
  onBlur,
  displayTypeId,
}: ThumbnailVariationItemProps) => {
  const shouldDisplayVariationText =
    variationGroup.displayTypeId === DisplayTypes.ImageAndLabel;
  const stockAwareTileBorderStyle = groupItem.isInStock
    ? `before:border-solid ${baseTileBorderStyling}`
    : `before:border-dashed before:border-[1px] ${baseTileBorderStyling}`;
  return (
    <li key={groupItem.itemNumber} className={cls("block min-h-10")}>
      <a
        onClick={() => {
          variationAnalyticsClickTracking(
            groupItem.seoLink,
            groupItem.variationText,
          );
        }}
        onFocus={() => onFocus(groupItem.itemNumber)}
        onBlur={() => onBlur(groupItem.itemNumber)}
        onMouseEnter={() => {
          updateActiveItemNumber(groupItem.itemNumber);
        }}
        onMouseLeave={() => updateActiveItemNumber(undefined)}
        suppressHydrationWarning
        data-testid={groupItem.variationText}
        href={getHrefTarget(groupItem, isSelected)}
        className={`flex flex-col items-center h-full no-underline box-border relative rounded focus-visible:before:border-[3px] focus-visible:before:border-[#005FCC] ${baseTileStyling} ${
          isSelected ? selectedTileBorderStyling : stockAwareTileBorderStyle
        } ${baseTileHoverStyling} ${
          displayTypeId === DisplayTypes.Tile
            ? "flex justify-center align-center"
            : ""
        }`}
        aria-current={isSelected ? "page" : "false"}
      >
        {displayTypeId === DisplayTypes.Tile ? (
          <div className="relative overflow-hidden flex justify-center align-center w-full">
            <span
              className="relative z-5 text-[13px] text-center text-black font-semibold leading-none p-2"
              style={{
                textShadow:
                  "0 1px 0 white, 1px 0 0 white, -1px 0 0 white, 0 -1px 0 white",
              }}
            >
              {groupItem.variationText}
            </span>
            {groupItem.isInStock ? null : (
              <div className="w-[50%] transform top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-gray-400 -rotate-45 absolute h-0 border-[1px] border-solid shadow-sm shadow-white" />
            )}
          </div>
        ) : (
          <Image
            className={cls(
              "box-border",
              "block",
              "h-auto",
              "m-0",
              "p-2",
              "w-full",
              "aspect-square",
              groupItem.isInStock ? "opacity-100" : "opacity-50",
            )}
            src={groupItem.thumbnail}
            style={
              shouldDisplayVariationText
                ? {}
                : { boxShadow: "0 2px 2px rgb(0 0 0 / 5%)" }
            }
            loading="lazy"
            height={1}
            width={1}
          />
        )}
        {groupItem.isInStock || displayTypeId === DisplayTypes.Tile ? null : (
          <div className="absolute w-[113%] h-0 right-[10%] top-[5%] border-[1px] border-solid border-[#949793] shadow-sm shadow-white -rotate-45 origin-top-right" />
        )}

        {shouldDisplayVariationText ? (
          <div className="flex-grow flex items-center justify-center mt-auto">
            <span
              data-testid="thumbnailVariationText"
              className={`text-center text-[12px] [@media(min-width:1150px)]:text-[13px] font-semibold leading-none justify-center pb-2 px-2 md ${
                !groupItem.isInStock ? "text-gray-600" : "text-gray-900"
              }`}
            >
              {groupItem.variationText}
            </span>
          </div>
        ) : null}
      </a>
    </li>
  );
};

export default ThumbnailVariationItem;
